// Split out of src/lib/utils.js (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). utils.js re-exports
// everything here, so existing imports keep working; new code may import from here directly.

import { nowIso, uid } from "./constants.js";
import { capStatusForNewEarning } from "./money.js";
import { todayStr, daysBetween, dateStrAddDays, weekStartOf, weekDatesFrom } from "./dates.js";

// --- Vocab spaced repetition (R3b) ---------------------------------------------------
// A mastered word (correctCount >= 2 — see App.jsx's onVocabWordResult) leaves the
// eligible test pool, then comes back for a one-question refresher 14 days after mastery,
// and every 45 days after each correct refresher. A WRONG refresher answer demotes the
// word back into normal rotation (correctCount drops to 1) until it's re-mastered.
// R6's Daily Question keeps its own casual resurfacing of mastered words — this is the
// systematic path. Words mastered before this feature existed get masteredAt backfilled
// to upgrade day in migrateState below, so their 14-day clock starts from the upgrade.
export const VOCAB_REFRESH_FIRST_DAYS = 14;
export const VOCAB_REFRESH_CYCLE_DAYS = 45;

export function vocabRefresherDue(word, today = todayStr()) {
  if (!word || (word.correctCount || 0) < 2 || !word.masteredAt) return false;
  if (word.lastRefreshedAt) return daysBetween(word.lastRefreshedAt, today) >= VOCAB_REFRESH_CYCLE_DAYS;
  return daysBetween(word.masteredAt, today) >= VOCAB_REFRESH_FIRST_DAYS;
}

export function habitsForChild(habits, childId) {
  return (habits || []).filter((h) => !h.childId || h.childId === childId);
}
// A "complete day" means every task that applies to this child was checked off that date.
// A child with no applicable tasks yet can never have a complete day (nothing to celebrate).

export function isHabitDayComplete(habitLog, habits, childId, dateStr) {
  const applicable = habitsForChild(habits, childId);
  if (applicable.length === 0) return false;
  const doneIds = new Set((habitLog || []).filter((l) => l.childId === childId && l.date === dateStr).map((l) => l.taskId));
  return applicable.every((h) => doneIds.has(h.id));
}
// Consecutive complete days counting back from today. If today isn't finished yet, it's
// simply not counted either way (yet) rather than breaking the streak mid-day.

export function currentHabitStreak(habitLog, habits, childId, today = todayStr()) {
  let streak = 0;
  let d = isHabitDayComplete(habitLog, habits, childId, today) ? today : dateStrAddDays(today, -1);
  while (isHabitDayComplete(habitLog, habits, childId, d)) {
    streak++;
    d = dateStrAddDays(d, -1);
  }
  return streak;
}

// ---- LB-13 (W2.6): raising the weekly streak reward ----
// ONE definition, called from toggleHabit (App.jsx) and streak repair (AdjustSections.jsx)
// inside their updaters. Idempotent per child-week: a request for this week already existing
// means nothing happens. Mode "auto" mirrors Learn & Earn exactly: under the cap the tokens
// are credited on the spot and the habit_reward request is written ALREADY APPROVED (so the
// week lock, the kid card's "reward already given" state and Recently resolved all keep
// working unchanged); at or over the cap it becomes a pending request flagged breachesCap,
// same as a learn_earn_reward. Mode "manual" is the original pending request. Returns
// "credited" | "pending" | null.
export function habitRewardModeOf(s) {
  return s && s.settings && s.settings.habitRewardMode === "auto" ? "auto" : "manual";
}
export function raiseHabitReward(s, child, weekStart, completeDays, threshold, reward, deviceId) {
  if (!child || !s) return null;
  if ((s.requests || []).some((r) => r.requestType === "habit_reward" && r.childId === child.id && r.weekStart === weekStart)) return null;
  const at = nowIso();
  const title = `Weekly streak reward (${completeDays}/${threshold} days)`;
  const base = { id: uid(), childId: child.id, requestType: "habit_reward", title, requestedAmount: reward, weekStart, timestamp: at };
  if (habitRewardModeOf(s) === "auto") {
    const cap = capStatusForNewEarning(child, s.transactions || [], s.requests || [], s.settings?.exchangeRate, reward);
    if (!cap.blocked && !cap.breaches) {
      s.requests.unshift({ ...base, status: "approved", resolvedAt: at, autoApproved: true });
      s.transactions.unshift({ id: uid(), deviceId: deviceId || null, childId: child.id, amount: reward, reason: title, type: "credit", balance: "token", timestamp: at, requestId: base.id, earnCap: true, autoApproved: true });
      child.tokenBalance = (child.tokenBalance || 0) + reward;
      return "credited";
    }
    s.requests.unshift({ ...base, title: `${title} — over weekly cap`, status: "pending", breachesCap: true });
    return "pending";
  }
  s.requests.unshift({ ...base, status: "pending" });
  return "pending";
}

export function habitWeekProgress(habitLog, habits, childId, today = todayStr()) {
  const weekStart = weekStartOf(today);
  const dates = weekDatesFrom(weekStart);
  const completeDays = dates.filter((d) => isHabitDayComplete(habitLog, habits, childId, d)).length;
  return { weekStart, dates, completeDays };
}
